'use client';

import { useCallback, useState } from 'react';
import { useSession } from 'next-auth/react';

import { useModalStore } from '@/store/modal.store';

export interface UseFanficRatingReturn {
  userRating: number;
  hoveredRating: number;
  isOpenCollectionDialog: boolean;
  handleStarClick: (rating: number) => void;
  handleStarHover: (rating: number) => void;
  handleStarLeave: () => void;
  openCollectionDialog: () => void;
  closeCollectionDialog: () => void;
}

export interface UseFanficRatingProps {
  initialRating: number;
  isInCollection: boolean;
  updateRating: (rating: number) => void;
}

/**
 * Rating stars + collection dialog gate for the fanfic page sidebar.
 */
export const useFanficRating = ({
  initialRating,
  isInCollection,
  updateRating,
}: UseFanficRatingProps): UseFanficRatingReturn => {
  const { data: session } = useSession();
  const { setIsOpenAuthDialog } = useModalStore();
  const [userRating, setUserRating] = useState<number>(initialRating);
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [isOpenCollectionDialog, setIsOpenCollectionDialog] = useState(false);

  const openCollectionDialog = useCallback(() => {
    setIsOpenCollectionDialog(true);
  }, []);

  const closeCollectionDialog = useCallback(() => {
    setIsOpenCollectionDialog(false);
  }, []);

  const handleStarClick = useCallback(
    (rating: number) => {
      if (!session?.user) {
        setIsOpenAuthDialog(true);
        return;
      }

      if (!isInCollection) {
        setIsOpenCollectionDialog(true);
      }

      setUserRating(rating);
      updateRating(rating);
    },
    [session?.user, isInCollection, setIsOpenAuthDialog, updateRating],
  );

  const handleStarHover = useCallback((rating: number) => {
    setHoveredRating(rating);
  }, []);

  const handleStarLeave = useCallback(() => {
    setHoveredRating(0);
  }, []);

  return {
    userRating,
    hoveredRating,
    isOpenCollectionDialog,
    handleStarClick,
    handleStarHover,
    handleStarLeave,
    openCollectionDialog,
    closeCollectionDialog,
  };
};
